import React, { useState } from 'react';
import {
  CalendarIcon,
  ClockIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  UserGroupIcon,
  MapPinIcon,
  TrashIcon,
  XMarkIcon,
  TicketIcon,
  UserIcon
} from '@heroicons/react/24/outline';

// Utility functions for better color accessibility
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

const getLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const getContrastRatio = (color1: string, color2: string): number => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  if (!rgb1 || !rgb2) return 1;
  
  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);
  
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  
  return (brightest + 0.05) / (darkest + 0.05);
};

const getAccessibleTextColor = (backgroundColor: string, isDark: boolean): string => {
  const whiteContrast = getContrastRatio(backgroundColor, '#ffffff');
  const blackContrast = getContrastRatio(backgroundColor, '#000000');
  const lightGrayContrast = getContrastRatio(backgroundColor, '#f3f4f6');
  const darkGrayContrast = getContrastRatio(backgroundColor, '#1f2937');
  
  // WCAG AA requires 4.5:1 for normal text, 3:1 for large text
  if (isDark) {
    if (whiteContrast >= 4.5) return 'text-white';
    if (lightGrayContrast >= 4.5) return 'text-gray-100';
    return 'text-gray-200';
  } else {
    if (blackContrast >= 4.5) return 'text-black';
    if (darkGrayContrast >= 4.5) return 'text-gray-900';
    return 'text-gray-800';
  }
};

interface EventCardData {
  id: number;
  title: string;
  start: string;
  start_time: string;
  end_time?: string;
  beschreibung?: string;
  location?: string;
  creator_name: string;
  creator_image?: string;
  creator: string;
  cotrainer?: string;
  cotrainer_name?: string;
  cotrainer_image?: string;
  participantCount: number;
  max_teilnehmer?: number;
  farbe?: string;
  // MyGuestCards specific fields
  cardName?: string;
  booking_date?: string;
  cardId?: number;
  kalender_id?: number;
}

interface EventCardProps {
  event: EventCardData;
  isNext?: boolean;
  showCancelButton?: boolean;
  showParticipantsButton?: boolean;
  participantCount?: number;
  loadingParticipants?: boolean;
  onCancel?: () => void;
  onToggleParticipants?: () => void;
  BASE_URL?: string;
  // MyGuestCards specific props
  isGuestCardStyle?: boolean;
  // Cancellation status props
  cancellationStatus?: {
    allowed: boolean;
    reason?: string;
  };
}

// Get user image URL helper
const getUserImageUrl = (userId: string, BASE_URL: string) => {
  const token = localStorage.getItem('jwt_token');
  return `${BASE_URL}/api/members/user-image-url/${userId}?size=S&token=${token}`;
};

// Enhanced Trainer Avatar Component with better UX and accessibility
const TrainerAvatar: React.FC<{ userId: string; name: string; size?: 'sm' | 'md'; BASE_URL: string }> = ({ 
  userId, 
  name, 
  size = 'sm',
  BASE_URL
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  
  const sizeClasses = size === 'sm' ? 'w-6 h-6 sm:w-8 sm:h-8' : 'w-8 h-8 sm:w-10 sm:h-10'; // Compact mobile size
  const textSize = size === 'sm' ? 'text-[10px] sm:text-xs' : 'text-xs sm:text-sm';
  
  const safeName = name || 'Unknown';
  const names = safeName.split(' ').filter(n => n.length > 0);
  const initials = names.length >= 2 
    ? `${names[0].charAt(0)}${names[names.length-1].charAt(0)}`.toUpperCase()
    : safeName.substring(0, 2).toUpperCase();
  
  const imageUrl = userId && BASE_URL ? getUserImageUrl(userId, BASE_URL) : null;
  
  // Fallback avatar with UserIcon
  if (imageError || !imageUrl) {
    return (
      <div
        className={`${sizeClasses} rounded-full bg-primary-600 flex items-center justify-center text-white shadow-sm ring-2 ring-white dark:ring-gray-700 flex-shrink-0`}
        title={safeName}
        aria-label={`Avatar for ${safeName}`}
      >
        <UserIcon className="h-3/5 w-3/5" />
      </div>
    );
  }
  
  return (
    <div className={`${sizeClasses} relative flex-shrink-0`}>
      {/* Loading skeleton with better UX */}
      {imageLoading && (
        <div className={`${sizeClasses} rounded-full bg-gray-200 dark:bg-gray-600 animate-pulse ring-2 ring-white dark:ring-gray-700`} />
      )}
      <img
        src={imageUrl}
        alt={`${safeName} Avatar`}
        className={`${sizeClasses} rounded-full object-cover ring-2 ring-white dark:ring-gray-700 shadow-sm transition-opacity duration-200 ${
          imageLoading ? 'opacity-0 absolute' : 'opacity-100'
        }`}
        onLoad={() => setImageLoading(false)}
        onError={() => {
          setImageError(true);
          setImageLoading(false);
        }}
        loading="lazy"
        title={safeName}
      />
    </div>
  );
};

const EventCard: React.FC<EventCardProps> = ({
  event,
  isNext = false,
  showCancelButton = false,
  showParticipantsButton = false,
  participantCount,
  loadingParticipants = false,
  onCancel,
  onToggleParticipants,
  BASE_URL = '',
  isGuestCardStyle = false,
  cancellationStatus
}) => {
  const eventDateTime = new Date(`${event.start}T${event.start_time || '00:00'}`);
  const isFutureEvent = eventDateTime > new Date();
  const dayOfWeek = eventDateTime.toLocaleDateString('de-DE', { weekday: 'short' });
  const formattedDate = eventDateTime.toLocaleDateString('de-DE');
  
  // Enhanced color system with accessibility
  const primaryColor = event.farbe || (isFutureEvent 
    ? isNext 
      ? '#10b981' // success-500
      : '#2563eb' // primary-500
    : '#2563eb'); // primary-500 auch für vergangene Events ohne farbe
    
  const isDarkTheme = document.documentElement.classList.contains('dark');

  return (
    <div
      className={`relative bg-white dark:bg-[#1c2432] rounded-lg sm:rounded-xl p-2 sm:p-4 border sm:border-2 overflow-hidden transition-all duration-200 hover:shadow-lg ${
        isFutureEvent
          ? isNext
            ? 'border-success-500'
            : 'border-primary-500'
          : 'border-gray-200 dark:border-gray-700'
      }`}
      style={{
        borderColor: primaryColor,
        borderLeftWidth: event.farbe ? '3px' : '1px'
      }}
    >
      {/* Status Badge */}
      <div 
        className="absolute top-0 right-0 px-1.5 sm:px-3 py-0.5 sm:py-2 rounded-bl-md sm:rounded-bl-xl text-[10px] sm:text-xs font-semibold flex items-center gap-0.5 sm:gap-1 min-h-[20px] sm:min-h-[32px]"
        style={{
          backgroundColor: primaryColor,
          color: isDarkTheme ? '#ffffff' : getAccessibleTextColor(primaryColor, isDarkTheme) === 'text-white' ? '#ffffff' : '#000000'
        }}
      >
        {isFutureEvent ? (
          <>
            {isNext ? (
              <>
                <ClockIcon className="h-2 w-2 sm:h-3 sm:w-3 flex-shrink-0" />
                <span className="truncate">Als Nächstes</span>
              </>
            ) : (
              <>
                <CalendarDaysIcon className="h-2 w-2 sm:h-3 sm:w-3 flex-shrink-0" />
                <span className="truncate">Kommend</span>
              </>
            )}
          </>
        ) : (
          <>
            <CheckCircleIcon className="h-2 w-2 sm:h-3 sm:w-3 flex-shrink-0" />
            <span className="truncate">Vergangen</span>
          </>
        )}
      </div>
      
      <div className="pr-12 sm:pr-24"> {/* Compact padding for mobile */}
        {/* Event Title - Improved Typography */}
        <h3 className="text-sm sm:text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1 sm:mb-3 leading-tight">
          {event.title || 'Unbenanntes Event'}
        </h3>
        
        {/* Date and Time - Ultra compact mobile layout */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mb-1 sm:mb-3">
          <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
            <CalendarIcon className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" 
              style={{ color: primaryColor }} />
            <span className="text-gray-700 dark:text-gray-200 font-medium truncate">
              {dayOfWeek}, {formattedDate}
            </span>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-sm">
            <ClockIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" 
              style={{ color: primaryColor }} />
            <span className="text-gray-700 dark:text-gray-200 font-medium">
              {event.start_time || '00:00'}{event.end_time && ` - ${event.end_time}`} Uhr
            </span>
          </div>
        </div>
        
        {/* Creator/Trainer Info or Guest Card Info */}
        {isGuestCardStyle ? (
          // MyGuestCards style - Card info and booking date with trainer info - Enhanced
          <>
            <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-3 flex-wrap">
              <TicketIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 text-gray-500 dark:text-gray-400" 
                style={{ color: primaryColor }} />
              <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-200 font-medium">
                {event.cardName || 'Gästekarte'}
              </span>
              {event.booking_date && (
                <span className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-300 italic">
                  • Gebucht {new Date(event.booking_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} {new Date(event.booking_date).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                </span>
              )}
            </div>
            
            {/* Trainer/Creator with Image for Guest Card Style */}
            <div className="flex items-center gap-1 sm:gap-3 mb-1 sm:mb-3">
              <TrainerAvatar userId={event.creator} name={event.creator_name || 'Unbekannt'} BASE_URL={BASE_URL} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 text-xs sm:text-sm">
                  <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {event.creator_name || 'Unbekannter Trainer'}
                  </span>
                  {event.cotrainer_name && event.cotrainer_name.trim() && (
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">+</span>
                      <TrainerAvatar userId={event.cotrainer || ''} name={event.cotrainer_name} BASE_URL={BASE_URL} />
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {event.cotrainer_name}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          // MyBookings style - Creator/trainer with images and additional info
          <>
            {/* Trainer/Creator with Image - Enhanced */}
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <TrainerAvatar userId={event.creator} name={event.creator_name || 'Unbekannt'} BASE_URL={BASE_URL} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 text-xs sm:text-sm">
                  <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {event.creator_name || 'Unbekannter Trainer'}
                  </span>
                  {event.cotrainer_name && event.cotrainer_name.trim() && (
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">+</span>
                      <TrainerAvatar userId={event.cotrainer || ''} name={event.cotrainer_name} BASE_URL={BASE_URL} />
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {event.cotrainer_name}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Additional Info - Ultra compact */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs sm:text-sm text-gray-600 dark:text-gray-300 mb-1 sm:mb-3">
              {event.location && (
                <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                  <MapPinIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 text-gray-500 dark:text-gray-400" 
                    style={{ color: primaryColor }} />
                  <span className="truncate font-medium">{event.location}</span>
                </div>
              )}
              
              <div className="flex items-center gap-1 sm:gap-2">
                <UserGroupIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 text-gray-500 dark:text-gray-400" 
                  style={{ color: primaryColor }} />
                <span className="font-medium">
                  {event.participantCount ?? 0}
                  {event.max_teilnehmer ? ` / ${event.max_teilnehmer}` : ''} 
                  <span className="hidden sm:inline">Teilnehmer</span>
                </span>
              </div>
            </div>
            
            {/* Description - Enhanced */}
            {event.beschreibung && (
              <div className="mb-2 sm:mb-3 p-2 sm:p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                  {event.beschreibung}
                </p>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Action Buttons - Ultra compact */}
      <div className="absolute bottom-1 right-1 sm:bottom-4 sm:right-4 flex gap-0.5 sm:gap-2">
        {showParticipantsButton && (
          <button
            onClick={onToggleParticipants}
            disabled={loadingParticipants}
            className="min-h-[28px] sm:min-h-[44px] min-w-[28px] sm:min-w-[44px] px-1 sm:px-3 py-0.5 sm:py-2 bg-gray-600 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded sm:rounded-lg text-[10px] sm:text-sm font-medium transition-all duration-200 flex items-center gap-0.5 sm:gap-2 hover:shadow-md active:scale-95"
            aria-label={`${(participantCount || event.participantCount) ?? 0} Teilnehmer anzeigen`}
          >
            {loadingParticipants ? (
              <div className="w-3 h-3 sm:w-4 sm:h-4 border border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <UserGroupIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="font-semibold text-[10px] sm:text-sm">{(participantCount ?? event.participantCount) ?? 0}</span>
              </>
            )}
          </button>
        )}
        
        {showCancelButton && (isFutureEvent || cancellationStatus?.allowed) && (
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={cancellationStatus?.allowed !== false ? onCancel : undefined}
              disabled={cancellationStatus?.allowed === false}
              className={`min-h-[28px] sm:min-h-[44px] px-2 sm:px-4 py-1 sm:py-2 rounded sm:rounded-lg text-[10px] sm:text-sm font-semibold transition-all duration-200 flex items-center gap-0.5 sm:gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                cancellationStatus?.allowed === false
                  ? 'bg-gray-400 dark:bg-gray-600 text-gray-600 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-error-600 hover:bg-error-700 text-white hover:shadow-md active:scale-95 focus:ring-error-500'
              }`}
              aria-label={cancellationStatus?.allowed === false ? 'Stornierung nicht möglich' : 'Buchung stornieren'}
            >
              {isGuestCardStyle ? (
                <>
                  <XMarkIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="truncate">Stornieren</span>
                </>
              ) : (
                <>
                  <TrashIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="truncate">Stornieren</span>
                </>
              )}
            </button>
            {cancellationStatus?.allowed === false && cancellationStatus.reason && (
              <p className="text-xs text-gray-500 dark:text-gray-400 text-right max-w-[140px] leading-tight">
                {cancellationStatus.reason}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventCard;